import React, { useState } from 'react';
import { formatPinyinWithTone } from '../utils/pinyinConverter';
import PinyinSelector from './PinyinSelector';

const PinyinGrid = ({ character, settings, index, onPinyinSelect }) => {
  const [showSelector, setShowSelector] = useState(false);
  const [selectorPosition, setSelectorPosition] = useState({ x: 0, y: 0 });

  const {
    cellSize,
    pinyinHeight,
    gridColor,
    pinyinColor,
    hanziColor,
    showPinyin,
    showPinyinLines,
    showPinyinBorder,
    showGridLines,
    showGridBorder,
    practiceMode,
    gridStyle = 'tianzige'
  } = settings;

  const { char, pinyin, isHanzi, tone } = character;

  // 计算尺寸
  const totalHeight = showPinyin ? cellSize + pinyinHeight : cellSize;
  const viewBoxWidth = cellSize;
  const viewBoxHeight = totalHeight;

  // 格子线条样式
  const gridLineStyle = {
    stroke: gridColor,
    strokeWidth: 1,
    fill: 'none'
  };

  const crossLineStyle = {
    stroke: gridColor,
    strokeWidth: 0.5,
    strokeDasharray: '4,4',
    fill: 'none'
  };

  // 格式化拼音 - 当toneType为symbol时，pinyin-pro已经返回带声调的拼音，直接使用
  const formattedPinyin = isHanzi && pinyin ? pinyin : '';
  const pinyinFontSize = pinyinHeight * 0.52;

  // 处理点击事件 - 显示多音字选择
  const handleClick = (event) => {
    if (!isHanzi || !character.allPinyins || character.allPinyins.length <= 1) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    // 计算弹出框位置（使用视口坐标）
    const rect = event.currentTarget.getBoundingClientRect();
    const x = rect.left + rect.width / 2 - 70; // 居中
    const y = rect.top - 10; // 上方显示

    // 确保不超出屏幕
    const adjustedX = Math.max(10, Math.min(x, window.innerWidth - 150));
    const adjustedY = Math.max(10, y);

    setSelectorPosition({ x: adjustedX, y: adjustedY });
    setShowSelector(true);
  };

  // 处理读音选择
  const handlePinyinSelect = (pinyinIndex) => {
    if (onPinyinSelect) {
      onPinyinSelect(index, pinyinIndex);
    }
    setShowSelector(false);
  };

  // 关闭选择器
  const handleCloseSelector = () => {
    setShowSelector(false);
  };

  // 根据练习模式决定汉字显示
  const getHanziDisplay = () => {
    switch (practiceMode) {
      case 'trace':
        return (
          <text
            x={cellSize / 2}
            y={cellSize / 2}
            textAnchor="middle"
            dominantBaseline="central"
            fill={hanziColor}
            opacity={0.3}
            fontSize={cellSize * 0.6}
            fontFamily="KaiTi, STKaiti, serif"
          >
            {char}
          </text>
        );
      case 'copy':
        return null; // 临摹模式不显示汉字
      default:
        return (
          <text
            x={cellSize / 2}
            y={cellSize / 2}
            textAnchor="middle"
            dominantBaseline="central"
            fill={hanziColor}
            fontSize={cellSize * 0.6}
            fontFamily="KaiTi, STKaiti, serif"
          >
            {char}
          </text>
        );
    }
  };

  // 非汉字不渲染（GridRenderer已经过滤了非汉字）
  if (!isHanzi) {
    return null;
  }

  const hasMultiplePinyins = isHanzi && character.allPinyins && character.allPinyins.length > 1;

  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      <svg
        width={cellSize}
        height={totalHeight}
        viewBox={`0 0 ${viewBoxWidth} ${viewBoxHeight}`}
        className="pinyin-grid-item"
        style={{
          overflow: 'visible',
          cursor: hasMultiplePinyins ? 'pointer' : 'default',
          transition: 'transform 0.2s'
        }}
        onClick={handleClick}
        onMouseOver={(e) => {
          if (hasMultiplePinyins) {
            e.currentTarget.style.transform = 'scale(1.05)';
          }
        }}
        onMouseOut={(e) => {
          e.currentTarget.style.transform = 'scale(1)';
        }}
      >
      {/* 拼音区域 - 四线三格 */}
      {showPinyin && (
        <g>
          {/* 背景 - 拼音外框独立控制 */}
          <rect
            x={0}
            y={0}
            width={cellSize}
            height={pinyinHeight}
            fill="white"
            stroke={showPinyinBorder ? gridColor : 'none'}
            strokeWidth={1}
          />

          {/* 四线三格 - 标准格式 */}
          {showPinyinLines && (
            <g>
              {/* 顶线 - 最上面的线（实线） */}
              <line x1={0} y1={0} x2={cellSize} y2={0} stroke={gridColor} strokeWidth={1.5} fill="none" />

              {/* 上中线 - 虚线 */}
              <line x1={0} y1={pinyinHeight / 3} x2={cellSize} y2={pinyinHeight / 3} stroke={gridColor} strokeWidth={0.5} strokeDasharray="6,3" fill="none" />

              {/* 中线 - 虚线 */}
              <line x1={0} y1={pinyinHeight * 2 / 3} x2={cellSize} y2={pinyinHeight * 2 / 3} stroke={gridColor} strokeWidth={0.5} strokeDasharray="6,3" fill="none" />

              {/* 底线 - 最下面的线（实线） */}
              <line x1={0} y1={pinyinHeight} x2={cellSize} y2={pinyinHeight} stroke={gridColor} strokeWidth={1.5} fill="none" />

              {/* 左右竖线（边框） */}
              <line x1={0} y1={0} x2={0} y2={pinyinHeight} stroke={gridColor} strokeWidth={1} fill="none" />
              <line x1={cellSize} y1={0} x2={cellSize} y2={pinyinHeight} stroke={gridColor} strokeWidth={1} fill="none" />
            </g>
          )}

          {/* 拼音文字 */}
          <text
            x={cellSize / 2}
            y={pinyinHeight * 0.6}
            textAnchor="middle"
            dominantBaseline="auto"
            fill={pinyinColor}
            fontSize={pinyinFontSize}
            fontFamily="Arial, 'PingFang SC', 'Microsoft YaHei', sans-serif"
            fontWeight="600"
          >
            {formattedPinyin}
          </text>
        </g>
      )}

      {/* 田字格区域 */}
      <g transform={showPinyin ? `translate(0, ${pinyinHeight})` : ''}>
        {/* 背景 - 田字格外框独立控制 */}
        <rect
          x={0}
          y={0}
          width={cellSize}
          height={cellSize}
          fill="white"
          stroke={showGridBorder ? gridColor : 'none'}
          strokeWidth={2}
        />

        {/* 田字格线条 */}
        {showGridLines && (
          <g>
            {gridStyle === 'tianzige' && (
              <>
                {/* 田字格：十字线 */}
                <line x1={cellSize / 2} y1={0} x2={cellSize / 2} y2={cellSize} stroke={gridColor} strokeWidth={0.5} strokeDasharray="4,4" fill="none" />
                <line x1={0} y1={cellSize / 2} x2={cellSize} y2={cellSize / 2} stroke={gridColor} strokeWidth={0.5} strokeDasharray="4,4" fill="none" />
              </>
            )}
            {gridStyle === 'mizige' && (
              <>
                {/* 米字格：十字线 + 对角线 */}
                <line x1={cellSize / 2} y1={0} x2={cellSize / 2} y2={cellSize} stroke={gridColor} strokeWidth={0.5} strokeDasharray="4,4" fill="none" />
                <line x1={0} y1={cellSize / 2} x2={cellSize} y2={cellSize / 2} stroke={gridColor} strokeWidth={0.5} strokeDasharray="4,4" fill="none" />
                <line x1={0} y1={0} x2={cellSize} y2={cellSize} stroke={gridColor} strokeWidth={0.5} strokeDasharray="4,4" fill="none" />
                <line x1={cellSize} y1={0} x2={0} y2={cellSize} stroke={gridColor} strokeWidth={0.5} strokeDasharray="4,4" fill="none" />
              </>
            )}
            {/* 方格和五宫格不需要内部线条 */}
          </g>
        )}

        {/* 汉字 */}
        {getHanziDisplay()}
      </g>

      {/* 多音字指示器 - 右上角小三角（导出时隐藏） */}
      {hasMultiplePinyins && (
        <g className="pinyin-indicator">
          <polygon
            points={`${cellSize - 12},0 ${cellSize},0 ${cellSize},12`}
            fill="#3b82f6"
            opacity="0.8"
          />
        </g>
      )}
    </svg>

      {/* 多音字选择器 */}
      {showSelector && (
        <PinyinSelector
          character={character}
          onSelect={handlePinyinSelect}
          onClose={handleCloseSelector}
          position={selectorPosition}
        />
      )}
    </div>
  );
};

export default PinyinGrid;
